"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Mail, Moon, Save, Sun, User as UserIcon } from "lucide-react";
import { api } from "@/lib/axios";
import type { User } from "@/lib/types/user";
import { useAuth } from "@/contexts/AuthContext";
import { useAuthStore } from "@/stores/authStore";
import { useBranches } from "@/hooks/api/useBranches";
import { useBranchesEnabled } from "@/hooks/api/useBranchesEnabled";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AccountActivePill,
  apiErr,
  buildRowRoles,
  initials,
  normalizeRoleId,
  type RoleRow,
} from "@/app/(dashboard)/admin/users/user-admin-shared";

export default function AdminUserProfilePage() {
  const { id: idParam } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const setUser = useAuthStore((s) => s.setUser);
  const userId = Number(idParam ?? 0);
  const { branchesEnabled } = useBranchesEnabled();
  const { data: branches = [] } = useBranches();

  const { data: rolesData, isLoading: rolesLoading } = useQuery({
    queryKey: ["admin", "roles"],
    queryFn: async () => {
      const { data: d } = await api.get<{ roles: RoleRow[] }>("/api/roles");
      return (d.roles ?? []).map((r) => ({
        ...r,
        id: Number((r as unknown as { id: string | number }).id),
      }));
    },
  });
  const rolesAll = rolesData ?? [];
  const roleOptions = useMemo(() => rolesAll.filter((r) => r.name !== "product_admin"), [rolesAll]);

  const {
    data: target,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["admin", "users", userId],
    queryFn: async () => {
      const { data: d } = await api.get<{ user: User }>(`/api/users/${userId}`);
      const u = d.user;
      return {
        ...u,
        role_id: normalizeRoleId(u.role_id as string | number | null | undefined) as unknown as User["role_id"],
      };
    },
    enabled: Number.isFinite(userId) && userId > 0,
  });

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState<number | "">("");
  const [branchId, setBranchId] = useState<number | "">("");
  const [active, setActive] = useState(true);
  const [uiTheme, setUiTheme] = useState<"light" | "dark">("light");
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (!target) {
      return;
    }
    setName(target.name ?? "");
    setEmail(target.email ?? "");
    const rid =
      normalizeRoleId(target.role_id as string | number | null | undefined) ??
      rolesAll.find((r) => r.name === target.role)?.id ??
      "";
    setRoleId(rid === "" ? "" : rid);
    const bid = target.branch_id;
    setBranchId(bid == null || bid === 0 ? "" : Number(bid));
    setActive(target.is_active === 1);
    setUiTheme(target.ui_theme === "dark" ? "dark" : "light");
    setPassword("");
  }, [target, rolesAll]);

  const rowRoles = useMemo(
    () => buildRowRoles(target ?? null, roleOptions, rolesAll),
    [target, roleOptions, rolesAll],
  );

  const save = useMutation({
    mutationFn: async () => {
      if (typeof roleId !== "number") {
        throw new Error("Choose a role");
      }
      const body: Record<string, unknown> = {
        name: name.trim(),
        email: email.trim(),
        role_id: roleId,
        is_active: active ? 1 : 0,
        ui_theme: uiTheme,
      };
      const roleName = rolesAll.find((r) => r.id === roleId)?.name;
      if (roleName) {
        body.role = roleName;
      }
      if (branchesEnabled) {
        body.branch_id = branchId === "" ? null : Number(branchId);
      }
      if (password.trim()) {
        body.password = password.trim();
      }
      const { data } = await api.put<{ user?: User }>(`/api/users/${userId}`, body);
      const updated = data?.user;
      if (!updated) {
        throw new Error("Server response missing user");
      }
      return updated;
    },
    onSuccess: async (updated) => {
      toast.success("Profile saved");
      setPassword("");
      const role_id = normalizeRoleId(updated.role_id as string | number | null | undefined) as unknown as User["role_id"];
      const merged: User = { ...updated, role_id };
      qc.setQueryData<User>(["admin", "users", userId], merged);
      await qc.invalidateQueries({ queryKey: ["admin", "users"] });
      if (me?.id === userId) {
        setUser(merged);
        void qc.invalidateQueries({ queryKey: ["auth", "me"] });
      }
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  if (!Number.isFinite(userId) || userId <= 0) {
    return (
      <div className="min-h-full bg-background p-6">
        <p className="text-sm text-destructive">Invalid user.</p>
        <Button variant="outline" className="mt-4" asChild>
          <Link to="/admin/users">Back to directory</Link>
        </Button>
      </div>
    );
  }

  if (isLoading || rolesLoading) {
    return (
      <div className="min-h-full space-y-6 bg-background p-6">
        <div className="h-10 w-48 animate-pulse rounded-lg bg-muted/60" />
        <div className="h-96 max-w-2xl animate-pulse rounded-2xl bg-muted/50" />
      </div>
    );
  }

  if (isError || !target) {
    return (
      <div className="min-h-full bg-background p-6">
        <p className="text-sm text-destructive">
          {(error as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "User not found."}
        </p>
        <Button variant="outline" className="mt-4" asChild>
          <Link to="/admin/users">Back to directory</Link>
        </Button>
      </div>
    );
  }

  const isSelf = me?.id === userId;

  return (
    <div className="min-h-full space-y-6 bg-background p-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" variant="ghost" size="sm" className="gap-1 pl-0" onClick={() => navigate("/admin/users")}>
          <ArrowLeft className="h-4 w-4" />
          Directory
        </Button>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-4">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-primary/15 text-lg font-bold text-primary">
            {initials(name || target.name)}
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Employee profile</h1>
            <p className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
              <Mail className="h-4 w-4 shrink-0 opacity-70" />
              <span className="truncate">{email}</span>
            </p>
            {target.created_at ? (
              <p className="mt-1 text-xs text-muted-foreground">Joined {target.created_at}</p>
            ) : null}
            {isSelf ? (
              <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                You are editing your own account. Sign-in session refreshes from saved data.
              </p>
            ) : null}
          </div>
        </div>
        <Button
          type="button"
          disabled={save.isPending || !name.trim() || !email.trim() || typeof roleId !== "number"}
          onClick={() => save.mutate()}
          className="shrink-0 gap-2"
        >
          <Save className="h-4 w-4" />
          {save.isPending ? "Saving…" : "Save changes"}
        </Button>
      </div>

      <div className="grid max-w-2xl gap-6">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <UserIcon className="h-5 w-5 text-primary" />
              <CardTitle>Account</CardTitle>
            </div>
            <CardDescription>Name, email, role, and sign-in access.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2 sm:col-span-2">
              <Label htmlFor="emp-name">Full name</Label>
              <Input id="emp-name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
            </div>
            <div className="grid gap-2 sm:col-span-2">
              <Label htmlFor="emp-email">Work email</Label>
              <Input
                id="emp-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </div>
            <div className="grid gap-2">
              <Label>Role</Label>
              <Select
                value={roleId === "" ? null : roleId}
                onValueChange={(v) => setRoleId(v == null ? "" : Number(v))}
                disabled={rowRoles.length === 0}
              >
                <SelectTrigger className="h-10 w-full">
                  <SelectValue placeholder="Choose role">
                    {(value: unknown) => {
                      const id = typeof value === "number" ? value : Number(value);
                      if (!Number.isFinite(id)) {
                        return null;
                      }
                      return rowRoles.find((r) => r.id === id)?.name ?? `Role #${id}`;
                    }}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {rowRoles.map((r) => (
                    <SelectItem key={r.id} value={r.id} label={r.name}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Account active</Label>
              <div className="flex h-10 items-center">
                <AccountActivePill checked={active} disabled={save.isPending} onCheckedChange={(v) => setActive(v)} />
              </div>
            </div>
            {branchesEnabled ? (
              <div className="grid gap-2 sm:col-span-2">
                <Label>Branch</Label>
                <Select
                  value={branchId === "" ? "__none__" : String(branchId)}
                  onValueChange={(v) => setBranchId(v === "__none__" ? "" : Number(v))}
                >
                  <SelectTrigger className="h-10 w-full">
                    <SelectValue placeholder="Assign branch" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__" label="— Not set">
                      — Not set
                    </SelectItem>
                    {branches.map((b) => (
                      <SelectItem key={b.id} value={String(b.id)} label={b.name}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <div className="grid gap-2 sm:col-span-2">
              <Label htmlFor="emp-pw">New password</Label>
              <Input
                id="emp-pw"
                type="password"
                placeholder="Leave blank to keep current password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Appearance</CardTitle>
            <CardDescription>Default light/dark for this user when they sign in.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant={uiTheme === "light" ? "secondary" : "outline"}
                size="sm"
                className="gap-2"
                onClick={() => setUiTheme("light")}
              >
                <Sun className="h-4 w-4" />
                Light
              </Button>
              <Button
                type="button"
                variant={uiTheme === "dark" ? "secondary" : "outline"}
                size="sm"
                className="gap-2"
                onClick={() => setUiTheme("dark")}
              >
                <Moon className="h-4 w-4" />
                Dark
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
